import { CreateDashboardDefinitionBase } from '../../../_shared/dashboard-definition.model';
import { axesView, composeHeading, createRouteMethodologyFilter, defineDashboard, resolveProduct } from '../../../_shared/dashboard-axes';
import { createTimeFilters, createPeriodLabelTransformers, createPeriodYearTransformers, resolveTimeFilter } from '../../../_shared/time-filters';
import {
  RankingValuePctScopeDashboardDataTransformer,
  SumSubMarketNameDataTransformer,
  UndefinedStateToSumGermanyDashboardDataTransformer,
} from '../../../../data_transformers/dashboard_data_transformer';
import {
  createIdNameTransformers,
  granularityHeadingPhrase,
  groupDetailOf,
  mapLabelKeyOf,
} from '../../../../dashboard-config/granularities/granularity-profile';
import { INDEX_VALUE_KEY } from '../index-metric.profile';
import { IndexMetricProfile } from './index-ranking-metric.profile';
import { DashboardIndexRankingDefinition } from './dashboard-index-ranking.definition';
import {
  DashboardIndexRankingShell,
  dashboardIndexRankingShellProfiles,
} from './dashboard-index-ranking-shell.profile';

export type CreateDashboardIndexRankingDefinitionInput =
  CreateDashboardDefinitionBase<DashboardIndexRankingShell, IndexMetricProfile>;

export function createDashboardIndexRankingDefinition(
  input: CreateDashboardIndexRankingDefinitionInput,
): DashboardIndexRankingDefinition {
  const shellKey = input.shell;
  const { axes, shell, metric } = resolveProduct(input, dashboardIndexRankingShellProfiles);
  const granularityId = axes.granularity.id;
  const valueKey = INDEX_VALUE_KEY;
  // Index datasets are yearly, also for Employed.
  const time = resolveTimeFilter(axes, 'single', { yearly: true });

  const defaultChartHeading = (filterValues: Map<string, any>) =>
    composeHeading(
      [
        'RANKING:',
        metric.headingMetricValue(filterValues),
        shell.headingWithoutScope ? axes.granularity.byPhrase : granularityHeadingPhrase(granularityId, filterValues),
      ],
      metric.headingTimeValue(filterValues),
    );
  const defaultMapHeading = (filterValues: Map<string, any>) =>
    composeHeading([metric.headingMetricValue(filterValues), filterValues.get('sub_market')]);

  return defineDashboard<DashboardIndexRankingDefinition>(axes, shell, {
    heading: (filterValues) =>
      metric.chartHeading?.(filterValues, shellKey) ??
      defaultChartHeading(filterValues),
    mapHeading: (filterValues) =>
      metric.mapHeading?.(filterValues, shellKey) ??
      defaultMapHeading(filterValues),
    filters: (deps) => [
      ...createTimeFilters(deps, time),
      ...shell.buildGranularityFilters(deps, { defaultState: axes.selections?.states?.[0] }),
      ...metric.createMetricFilters(shellKey),
      createRouteMethodologyFilter(deps),
    ],
    preFilterTransformers: (deps) => [
      new UndefinedStateToSumGermanyDashboardDataTransformer(),
      ...(metric.createPreFilterExtras?.(deps) ?? []),
      ...createPeriodLabelTransformers(deps, axes),
    ],
    postFilterTransformers: (deps) => [
      ...metric.createChartValueTransformers(valueKey, shellKey),
      ...metric.createIndexValueDisplayTransformers(),
      ...metric.createMetricValueDisplayTransformers(valueKey),
      new SumSubMarketNameDataTransformer(deps.localDatasetsService),
      new RankingValuePctScopeDashboardDataTransformer({ granularityId, resultColumnKey: 'sharePctScope' }),
      ...metric.createRankTransformers(valueKey),
      ...createPeriodYearTransformers(axes),
      ...metric.createMetricLabelTransformers(),
      ...createIdNameTransformers(deps, granularityId),
    ],
    exportTransformers: (_deps) =>
      metric.createExportTransformers(shellKey),
    view: {
      valueKey,
      shellKey,
      ...axesView(axes),
      groupDetail: groupDetailOf(granularityId, 'name'),
      chartSyncable: shell.chartSyncable,
      chartHasSlider: shell.chartHasSlider,
      mapValueLabelKey: shell.mapValueLabelKey ?? mapLabelKeyOf(granularityId),
      hideMapValueDisplay: shell.hideMapValueDisplay,
      popup: metric.popup,
    },
  });
}
